import React from "react";
import {
  Button,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { ISendEmailDialogProps } from "./SendEmailDialog.types";
import { RightPanel } from "../../../../components/RightPanel";
import { useFormik } from "formik";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../components/formsComponents";
import { EmailAutocompleteInput } from "./EmailAutocompleteInput";
import { ErrorMessage } from "../../../../components/ErrorMessage/ErrorMessage";
import { Editor } from "../../../../components/Editor/Editor";
import { FileUpload } from "../../../../features/FileUpload/components/FileUpload";
import { IFileUpload } from "../../../../features/FileUpload/components/FileUpload.type";
import {
  triggerErrorDialogMessages,
  triggerSuccessDialogMessages,
} from "../../../../helpers";
import { useDispatchWrapper } from "../../../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
} from "../../../../redux/messages/messagesActions";
import {
  forwardOutlookEmail,
  sendOutlookEmail,
} from "../../../../redux/outlook/outlookThunk";
import { APIError } from "../../../../constants/types";
import {
  IOutlookEmailComposePayload,
} from "../../../../redux/outlook/outlook.types";
import { fileToBase64 } from "../../../../helpers/outlook";
import { uploadMultipleFile } from "../../../../features/FileUpload/utils";
import { DisplayAttachments } from "../DisplayAttachments/DisplayAttachments";
import { IStoreState } from "../../../../redux/initialStoreState";
import { fetchAllSignatures } from "../../../../redux/signature/signatureThunk";
import { LoadState } from "../../../../constants/enums";
import { api } from "../../../../api/api";
import {
  ICreateTemplate,
  fetchTemplateListAllTempsAsync,
} from "../../../../redux/templates";


export const SendEmailDialog: React.FC<ISendEmailDialogProps> = ({
  open,
  reply,
  initialTo,
  onClose,
  onComplete,
  isForward = false,
  messageData,
}) => {
  const [showCc, setShowCc] = React.useState(false);
  const [showBcc, setShowBcc] = React.useState(false);
  const [attachments, setAttachments] = React.useState<IFileUpload[]>([]);
  const dispatch = useDispatchWrapper();
  const signatureInsertedRef = React.useRef(false);
  const isComposeMode = open && !messageData && !isForward;
  const { allSignatures, allSignaturesLoading } = useSelector(
    (storeState: IStoreState) => storeState.signature,
  );
  const templates = useSelector(
    (storeState: IStoreState) => storeState.templates.list,
  );
  const {
    values,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
    errors,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      to: initialTo && initialTo.trim() !== '' ? [initialTo] : reply ? [reply] : [],
      cc: [] as string[],
      bcc: [] as string[],
      subject: "",
      body: "",
      templateId: null as string | null,
    },
    validate: (formValues) => {
      const formErrors: {
        to?: string;
        body?: string;
      } = {};

      if (formValues.to.length === 0) {
        formErrors.to = "Please add at least one recipient.";
      }

      if (!formValues.body.trim()) {
        formErrors.body = "Message body is required.";
      }

      return formErrors;
    },
    onSubmit: async (formValues) => {
      if (isForward) {
        try {
          dispatch(saveLoaderProgress());
          await dispatch(
            forwardOutlookEmail({
              to: formValues.to,
              cc: formValues.cc,
              comment: formValues.body,
              messageId: messageData?.id || "",
            }),
          ).unwrap();

          triggerSuccessDialogMessages(
            "Success! We’ve sent your email.",
            dispatch,
          );
          onComplete();
          onClose();
        } catch (err) {
          triggerErrorDialogMessages(err as APIError, dispatch);
        } finally {
          dispatch(saveLoaderCompleted());
        }
        return;
      }

      const mappedAttachments = await Promise.all(
        attachments
          .filter((item) => !!item.file)
          .map(async (item) => {
            const contentBytes = await fileToBase64(item.file as File);
            return {
              name: item.file?.name || item.name || "attachment",
              contentType: item.file?.type || "application/octet-stream",
              contentBytes,
            };
          }),
      );

      const payload: IOutlookEmailComposePayload = {
        to: formValues.to,
        cc: showCc ? formValues.cc : [],
        bcc: showBcc ? formValues.bcc : [],
        subject: formValues.subject.trim(),
        body: formValues.body,
        attachments: mappedAttachments,
      };

      // Step 1: upload attachments to S3 first (non-blocking on failure)
      let attachmentUrls: string[] = [];
      if (attachments.length > 0) {
        try {
          const uploadResult = await uploadMultipleFile(attachments, "EMAIL_ATTACHMENT", { module_name: "outlook" ,"file_name":"email"});
          attachmentUrls = uploadResult.paths.filter((p): p is string => p !== null);
        } catch {
          // S3 upload failure is non-blocking — Outlook send will still proceed
        }
      }

      // Step 2: send via Outlook API
      try {
        dispatch(saveLoaderProgress());
        await dispatch(
          sendOutlookEmail({
            payload,
          }),
        ).unwrap();

        triggerSuccessDialogMessages(
          "Success! We’ve sent your email.",
          dispatch,
        );
        onComplete();
        onClose();
      } catch (err) {
        triggerErrorDialogMessages(err as APIError, dispatch);
      } finally {
        dispatch(saveLoaderCompleted());
      }
    },
  });


  // Fetch signatures when the dialog opens in compose mode (if not already loaded).
  // Insertion is handled either by the form-reset effect (cached case) or the
  // loading-complete effect below (first-time fetch case).
  React.useEffect(() => {
    if (!isComposeMode) {
      return;
    }

    // Already cached — form-reset effect already pre-populated the body
    if (allSignaturesLoading === LoadState.Loaded) {
      return;
    }

    // Not yet loaded — fetch; the loading-complete effect will insert once done
    dispatch(fetchAllSignatures());
  }, [open]);

  // Insert signature once the fetch completes (first-time load only).
  // Also resets the ref when the dialog closes so the next open starts fresh.
  React.useEffect(() => {
    if (!open) {
      signatureInsertedRef.current = false;
      return;
    }

    if (
      isComposeMode &&
      allSignaturesLoading === LoadState.Loaded &&
      !signatureInsertedRef.current
    ) {
      const defaultSig = allSignatures.find((s) => s.is_default === 1);
      if (defaultSig) {
        setFieldValue("body", `<br/><br/>${defaultSig.html_content}`);
      }
      signatureInsertedRef.current = true;
    }
  }, [isComposeMode, allSignaturesLoading, allSignatures]);

  React.useEffect(() => {
    if (messageData) {
      setValues((prevValues) => ({
        ...prevValues,
        subject: isForward
          ? `Fwd: ${messageData.subject}`
          : messageData.subject,
        body: isForward
          ? `<br/><br/>---------- Forwarded message ----------<br/>From: ${messageData.from.emailAddress.name} &lt;${messageData.from.emailAddress.address}&gt;<br/>Date: ${new Date(
              messageData.createdDateTime,
            ).toLocaleString()}<br/>Subject: ${messageData.subject}<br/><br/>${messageData.body.content}`
          : messageData.body.content,
      }));
    } else if (open) {
      // Determine the signature body to pre-populate (if already cached)
      const defaultSig =
        allSignaturesLoading === LoadState.Loaded && allSignatures.length > 0
          ? allSignatures.find((s) => s.is_default === 1)
          : undefined;

      setValues({
        to: initialTo && initialTo.trim() !== '' ? [initialTo] : reply ? [reply] : [],
        cc: [],
        bcc: [],
        subject: "",
        body: defaultSig ? `<br/><br/>${defaultSig.html_content}` : "",
        templateId: null,
      });
      // Mark signature as inserted so the signature effects don't overwrite
      signatureInsertedRef.current = !!defaultSig;
      setShowCc(false);
      setShowBcc(false);
      setAttachments([]);
    }
  }, [open, reply, initialTo, setValues]);

  // Fetch templates when the dialog opens
  React.useEffect(() => {
    if (open) {
      dispatch(fetchTemplateListAllTempsAsync());
    }
  }, [open, dispatch]);

  const handleTemplateChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    setValues({
      ...values,
      subject: "",
      body: "",
      templateId: value || null,
    });
  };

  // Fetch template details and populate subject/body when templateId changes
  React.useEffect(() => {
    const templateId = values.templateId;
    if (!templateId) {
      return;
    }

    const fetchTemplate = async () => {
      try {
        dispatch(saveLoaderProgress());
        const res = await api.get(
          `/templates/get-templates?template_code=${templateId}`,
        );
        const list: ICreateTemplate[] = res.data.data;
        if (list.length > 0) {
          const template = list[0];
          setValues((prev) => ({
            ...prev,
            subject: template.template_subject,
            body: template.body as string,
          }));
        }
      } catch (err) {
        console.log(err);
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

    fetchTemplate();
  }, [values.templateId, dispatch]);

  return (
    <RightPanel
      open={open}
      onClose={onClose}
      heading="Compose Email"
      subHeading="Write and send your message"
      isWrappedWithForm
      onFormSubmit={handleSubmit}
      width="45%"
      actionButtons={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Button variant="contained" type="submit" fullWidth>
              Send
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button variant="contained" onClick={onClose} fullWidth>
              Cancel
            </Button>
          </Grid>
        </Grid>
      )}
    >
      <Grid container>
        <Grid item xs={12} md={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <CustomFormLabel>To</CustomFormLabel>
            <Box display="flex" gap={1}>
              <Typography
                variant="body1"
                sx={{ cursor: "pointer", fontWeight: 800 }}
                onClick={() => setShowCc((value) => !value)}
              >
                Cc
              </Typography>
              <Typography
                variant="body1"
                sx={{ cursor: "pointer", fontWeight: 800 }}
                onClick={() => setShowBcc((value) => !value)}
              >
                Bcc
              </Typography>
            </Box>
          </Box>
          <EmailAutocompleteInput
            value={values.to}
            onChange={(emails) => setFieldValue('to', emails)}
            placeholder="Search or enter email addresses"
            error={Boolean(errors.to)}
            helperText={errors.to as string}
          />
        </Grid>

        {showCc && (
          <Grid item xs={12} md={12}>
            <CustomFormLabel>CC</CustomFormLabel>
            <EmailAutocompleteInput
              value={values.cc}
              onChange={(emails) => setFieldValue('cc', emails)}
              placeholder="Enter CC emails"
            />
          </Grid>
        )}

        {showBcc && (
          <Grid item xs={12} md={12}>
            <CustomFormLabel>BCC</CustomFormLabel>
            <EmailAutocompleteInput
              value={values.bcc}
              onChange={(emails) => setFieldValue('bcc', emails)}
              placeholder="Enter BCC emails"
            />
          </Grid>
        )}

        <Grid item xs={12} md={12}>
          <CustomFormLabel>Template</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={values.templateId}
            name="templateId"
            onChange={handleTemplateChange}
            placeholder="Choose Template"
            displayEmpty
            options={templates.map((item) => {
              return { label: item.template_name, value: item.template_code };
            })}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <CustomFormLabel>Subject</CustomFormLabel>
          <CustomTextField
            fullWidth
            placeholder="Subject"
            name="subject"
            value={values.subject}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <CustomFormLabel>Message</CustomFormLabel>
          <Editor
            content={values.body}
            showSignatureWidget={true}
            onChange={(html: string) => setFieldValue("body", html)}
          />
          {errors.body && <ErrorMessage value={errors.body} />}
        </Grid>

        {isForward && messageData ? (
          <>
           <DisplayAttachments attachments={messageData.attachments} />
          </>
        ) : (
          <Grid item xs={12} md={12}>
            <CustomFormLabel>Attachments</CustomFormLabel>
            <FileUpload
              multiple
              value={attachments}
              onMultiChange={setAttachments}
            />
          </Grid>
        )}
      </Grid>
    </RightPanel>
  );
};
